import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";

import { cn } from "./utils";

const buttonVariants = cva(
  // Icons are standardized by the atom: every <svg> inside a button is forced
  // to one size per button-size (overriding call-site h-4/size-4/etc), so icons
  // look identical everywhere. leading-none + text-box leading-trim center the
  // label optically (line box trimmed to cap→baseline).
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full type-body font-medium leading-none [text-box:trim-both_cap_alphabetic] transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-3.5 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "btn-dim text-primary-foreground [--btn-base:var(--primary)]",
        // Solid red, white label — clear danger, fixes the old dark-on-red.
        destructive:
          "btn-dim text-white [--btn-base:var(--destructive)]",
        outline:
          "btn-dim text-foreground [--btn-base:var(--background)] [--btn-ring:var(--border)]",
        // Flat neutral chip — a hairline-bordered control with a recessed surface-2
        // fill and NO dimensional gloss (unlike `outline`, which is `btn-dim`). The
        // muted icon lifts to surface-3 + foreground on hover, mirroring the
        // ViewToggle's active-segment color, so a toolbar of view toggles + filter /
        // utility icons (`SectionActions`) reads as ONE flat set. Radius is owned by
        // `size` (pair with `icon-tb` for the tight-cornered toolbar look).
        flat:
          "border border-border bg-surface-2 text-muted-foreground hover:bg-surface-3 hover:text-foreground",
        secondary:
          "btn-dim text-foreground [--btn-base:var(--secondary)] [--btn-ring:var(--border)]",
        ghost:
          "hover:bg-accent hover:text-foreground dark:hover:bg-accent/50",
        // Ghost with a muted RESTING label — the quiet tertiary affordance that
        // lives beside a heading (a SectionCard's "All runs ›" nav action) and
        // only claims foreground on hover. A `text-muted-foreground` className
        // on `ghost` trips check-button-consistency, so the resting color is an
        // atom variant (the `icon-tb-tight` precedent).
        "ghost-muted":
          "text-muted-foreground hover:bg-accent hover:text-foreground dark:hover:bg-accent/50",
        link: "text-foreground underline-offset-4 hover:underline",
        // Low-emphasis negative: red label, red/10 fill, red/30 ring. Pairs
        // with `destructive` as the lighter weight of the same danger color.
        reject:
          "border border-destructive/30 bg-destructive/10 text-destructive hover:bg-destructive/20 hover:border-destructive/50 active:bg-destructive/25",
        // Low-emphasis positive: green label, green/10 fill, green/30 ring — the
        // mirror of `reject` so an accept/reject decision pair reads as a
        // balanced green-vs-red set (same weight, same shape).
        accept:
          "border border-success/30 bg-success/10 text-success-fg hover:bg-success/20 hover:border-success/50 active:bg-success/25",
        // Section-accented solid: the dimensional fill painted from the section
        // hue published as `--section` (via `sectionAccentVars(slug)` on an
        // ancestor), with the readable label color `--section-fg`. The ring +
        // gradient derive from `--btn-base` automatically, so one accent value
        // drives the whole button across the nine-color palette. Falls back to
        // the primary when no `--section` is in scope. → docs/design/BUTTON-SYSTEM.md.
        section:
          "btn-dim text-[color:var(--section-fg,var(--primary-foreground))] [--btn-base:var(--section,var(--primary))]",
        // Low-emphasis section accent — the `accept`/`reject` soft pattern in the
        // section hue (accent label, accent/12 fill, accent/32 ring), painted by
        // the `.btn-section-soft` utility off `--section` (globals.css, mirroring
        // `.btn-dim`). The quieter weight for a function that isn't due, so its
        // button still reads as the department's color without shouting.
        "section-outline": "btn-section-soft",
        // The global "Play" CTA — the single, deliberately-special "go to work"
        // button. A near-black → brand-violet gradient with a persistent brand-violet
        // hairline ring that brightens on hover (definition by contrast, no motion). The whole
        // treatment lives in `.btn-play` (globals.css) so the call site stays a
        // clean variant, never a className repaint. → docs/design/BUTTON-SYSTEM.md.
        play: "btn-play text-white",
        // The first-run takeover primary — the CTA on the dark onboarding/auth
        // flows (login · welcome · onboarding). A premium dimensional fill in the
        // BRAND blue→purple (rides `--primary`, shifting toward violet). The
        // fill/shadow/label live in `.btn-graphite` (globals.css, mirroring
        // `.btn-dim`); radius + size come from the atom. (Name retained for
        // call-site stability — it renders the brand color, not graphite.)
        // → /styleguide/onboarding-language.
        graphite: "btn-graphite",
      },
      size: {
        // Control-height scale: dense 20 (h-5) · small 24 (h-6) · STANDARD 28
        // (h-7) · large 36 (h-9). Heights match Input/Select so buttons line up
        // with form fields in a row. Icon size scales with the button. Every
        // size is fully rounded — text buttons read as stadium pills, icon
        // buttons as circles (radius inherited from the base `rounded-full`).
        default: "h-7 px-3 has-[>svg]:px-2.5",
        xs: "h-5 gap-1 px-2 has-[>svg]:px-1.5 [&_svg]:size-3",
        sm: "h-6 gap-1.5 px-2.5 has-[>svg]:px-2 [&_svg]:size-3",
        // Card-header nav action — `sm` density at the STANDARD 28px height, so
        // the button fills a SectionCard's fixed h-7 title row exactly
        // (SectionNav's "All runs ›"). An `h-7` className on `size="sm"` is the
        // override the gate forbids; the height/density pairing is a size.
        nav: "h-7 gap-1.5 px-2.5 has-[>svg]:px-2 [&_svg]:size-3",
        lg: "h-9 px-4 has-[>svg]:px-3 [&_svg]:size-4",
        icon: "size-7",
        // Toolbar icon — `icon` (28px) sizing for dense action-bar clusters
        // (`SectionActions`); a full circle like every icon button.
        "icon-tb": "size-7",
        // `icon-tb` with the glyph one step down (12px vs the 14px standard) —
        // for FILLED glyphs (the nav Play triangle), which carry more visual
        // weight than stroke icons at the same size. A `[&_svg]:size-3`
        // className override trips check-button-consistency, so the tighter
        // glyph is an atom size variant (the `icon-round` precedent).
        "icon-tb-tight": "size-7 [&_svg]:size-3",
        "icon-xs": "size-5 [&_svg]:size-3",
        "icon-sm": "size-6 [&_svg]:size-3",
        "icon-lg": "size-9 [&_svg]:size-4",
        // Circular icon buttons at a fixed 32/28px — the floating nav capsule's
        // action affordance (Play). Radius now matches every icon button; the
        // distinct box size is what these variants pin.
        "icon-round": "size-8",
        "icon-round-sm": "size-7 [&_svg]:size-3.5",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot.Root : "button";
  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
